import React from 'react';
import {
  buttonStyles,
  Inline,
  LazyImage,
  MediaObject,
  Stack,
  Text,
  useDevice,
  useViewport,
  Viewport,
} from '@treatwell/ui';
import { ChannelLoyalty } from 'js/model/rainbow/content/ChannelOutput';
import { CMSTreatwellRewardsPage } from 'js/model/cms/cms-treatwell-rewards';
import clsx from 'clsx';
import { LoyaltyContainer } from '../LoyaltyContainer';
import { SectionHeader } from '../SectionHeader';
import styles from './RedeemTreats.module.css';
import downloadDeCH from './assets/de_CH-download.webp';
import downloadDe from './assets/de-download.webp';
import downloadElGR from './assets/el_GR-download.webp';
import downloadEnGB from './assets/en_GB-download.webp';
import downloadEnIE from './assets/en_IE-download.webp';
import downloadEsES from './assets/es_ES-download.webp';
import downloadFrFR from './assets/fr_FR-download.webp';
import downloadItIT from './assets/it_IT-download.webp';
import downloadLtLT from './assets/lt_LT-download.webp';
import downloadNlNL from './assets/nl-download.webp';
import downloadPtPT from './assets/pt_PT-download.webp';
import downloadItCh from './assets/it_CH-download.webp';
import downloadFrCh from './assets/fr_CH-download.webp';
import appQRcode from './assets/app-download-qr.webp';
import mobileIcon from './assets/mobile-icon.webp';
import { DOWNLOAD_APP_LINK } from '../constants';
import { trackDownloadApp } from './tracking';

const localesMap: Record<string, string> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  en_GB: downloadEnGB,
  en_IE: downloadEnIE,
  fr_FR: downloadFrFR,
  es_ES: downloadEsES,
  it_IT: downloadItIT,
  de_CH: downloadDeCH,
  de_AT: downloadDe,
  de_DE: downloadDe,
  lt_LT: downloadLtLT,
  en_BE: downloadEnIE,
  fr_BE: downloadFrFR,
  nl_BE: downloadNlNL,
  en_NL: downloadEnIE,
  nl_NL: downloadNlNL,
  pt_PT: downloadPtPT,
  el_GR: downloadElGR,
  it_CH: downloadItCh,
  fr_CH: downloadFrCh,
};

type RedeemTreatsProps = {
  title: string;
  description: string;
  downloadImg: string;
  downloadAppText: { mobile: string; desktop: string };
};

const RedeemTreatsSection = ({
  title,
  description,
  downloadImg,
  downloadAppText,
}: RedeemTreatsProps) => {
  const { isMobile } = useDevice();
  const isMobileViewport = useViewport({
    device: ['mobile', 'tablet'],
    serverRender: isMobile,
  });
  const linkStyles = clsx(buttonStyles.button, buttonStyles.md, styles.link);

  return (
    <section className={styles.root}>
      <LoyaltyContainer>
        <div className={styles.container}>
          <SectionHeader
            align="center"
            title={title}
            description={description}
          />
          <Viewport
            device={['mobile', 'tablet']}
            serverRender={isMobileViewport}
          >
            <a
              href={DOWNLOAD_APP_LINK}
              className={linkStyles}
              data-cy="RedeemTreatsAppLink"
              onClick={async (event: React.MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                const target = event.target as HTMLAnchorElement;
                await trackDownloadApp();
                window.location.href = target.href;
              }}
            >
              {downloadAppText.mobile}
            </a>
          </Viewport>
          <Inline align="center" justify="between" className={styles.width}>
            <LazyImage
              width="512"
              height="390"
              src={downloadImg}
              alt=""
              className={styles.img}
            />
            <Viewport device="desktop" serverRender={!isMobileViewport}>
              <Stack space="base">
                <div className={styles.appDownload}>
                  <MediaObject align="center" space="2md">
                    <img src={mobileIcon} alt="" width="24" height="24" />
                    <Text
                      as="p"
                      type="bodyHeavy"
                      className={styles.appDownloadText}
                    >
                      {downloadAppText.desktop}
                    </Text>
                  </MediaObject>
                </div>
                <LazyImage
                  src={appQRcode}
                  width="246"
                  height="246"
                  alt={DOWNLOAD_APP_LINK}
                  className={styles.img}
                />
              </Stack>
            </Viewport>
          </Inline>
        </div>
      </LoyaltyContainer>
    </section>
  );
};
type Props = {
  channel: ChannelLoyalty;
  cmsValues: CMSTreatwellRewardsPage;
};
export const RedeemTreatsSectionContainer = ({ channel, cmsValues }: Props) => {
  const img = localesMap[channel.locale];
  const cms = cmsValues.page['treatwell-rewards']['redeem-treats'];
  return (
    <RedeemTreatsSection
      title={cms.title}
      description={cms.description}
      downloadImg={img}
      downloadAppText={{
        desktop: cms['download-app-text'].desktop,
        mobile: cms['download-app-text'].mobile,
      }}
    />
  );
};
